import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  Play,
  Copy,
  RefreshCw,
  Cpu,
  Clock,
  DollarSign,
  Hash,
  FileText,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TestCase, TestSession, ModelResponse, AgentType, ModelType } from '@/types/ai-admin';
import { createTestSession, callTestQueryEdgeFunction, updateTestSession } from '@/services/aiTestingService';
import { createComponentLogger } from '@/utils/logger';
import { toast } from 'sonner';

const log = createComponentLogger('ModelComparison', 'ai-testing');

interface ModelComparisonProps {
  testCases: TestCase[];
  onSessionComplete: (session: TestSession) => void;
}

interface ComparisonResult {
  model: string;
  response: ModelResponse;
  rank: number;
  relativeSpeed: number;
  relativeCost: number;
}

export default function ModelComparison({ testCases: _testCases, onSessionComplete }: ModelComparisonProps) {
  const [query, setQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentType>('encompass_helper');
  const [selectedModels, setSelectedModels] = useState<ModelType[]>(['claude-haiku-4-5-20251001', 'claude-sonnet-4-5-20250929']);
  const [isComparing, setIsComparing] = useState(false);
  const [results, setResults] = useState<ComparisonResult[]>([]);
  const [winner, setWinner] = useState<ModelType | null>(null);

  const models: Array<{ value: ModelType; label: string; cost: number; speed: string }> = [
    { value: 'claude-haiku-4-5-20251001', label: 'Claude 4.5 Haiku', cost: 1.00, speed: 'Fast' },
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude 4.5 Sonnet', cost: 3.00, speed: 'Balanced' },
    { value: 'claude-opus-4-1-20250805', label: 'Claude 4.1 Opus', cost: 15.00, speed: 'Thorough' }
  ];

  const agents: Array<{ value: AgentType; label: string }> = [
    { value: 'encompass_helper', label: 'Encompass Helper' },
    { value: 'pre_approval', label: 'Pre-Approval Assistant' },
    { value: 'document_checklist', label: 'Document Checklist' },
    { value: 'mortgage_education', label: 'Mortgage Education' },
    { value: 'investor_guidelines', label: 'Investor Guidelines' },
    { value: 'wisr', label: 'Wisr' },
    { value: 'general', label: 'General Assistant' }
  ];

  // Toggle model selection
  const toggleModel = (modelValue: ModelType) => {
    setSelectedModels(prev => {
      if (prev.includes(modelValue)) {
        return prev.filter(m => m !== modelValue);
      } else {
        return [...prev, modelValue];
      }
    });
  };

  // Run comparison
  const runComparison = async () => {
    if (!query.trim() || selectedModels.length < 2) {
      log.warn('Cannot run comparison: invalid query or insufficient models');
      return;
    }

    setIsComparing(true);
    setResults([]);
    setWinner(null);
    const startTime = Date.now();

    try {
      log.info('Starting model comparison', {
        models: selectedModels,
        agent: selectedAgent,
        modelCount: selectedModels.length
      });

      // Create test session
      const sessionId = await createTestSession([], 'comparison');

      // Run all models in parallel using Promise.all
      const responses = await Promise.all(
        selectedModels.map(modelValue =>
          callTestQueryEdgeFunction(
            query,
            modelValue,
            selectedAgent,
            sessionId
          ).catch(error => {
            log.error('Model query failed', { model: modelValue }, error as Error);
            // Return error response for failed models
            return {
              id: `error_${Date.now()}_${modelValue}`,
              query,
              model: modelValue,
              response: `Error: ${(error as Error).message}`,
              responseTime: 0,
              cost: 0,
              tokensInput: 0,
              tokensOutput: 0,
              success: false,
              documentsUsed: [],
              timestamp: new Date().toISOString()
            } as ModelResponse;
          })
        )
      );

      // Build comparison results
      const comparisonResults: ComparisonResult[] = responses.map((response, index) => ({
        model: selectedModels[index],
        response,
        rank: 0,
        relativeSpeed: 0,
        relativeCost: 0
      }));

      // Calculate rankings and relative metrics
      const sortedByScore = [...comparisonResults]
        .filter(r => r.response.success) // Only rank successful responses
        .sort((a, b) => (b.response.score || 0) - (a.response.score || 0));

      const successfulResults = comparisonResults.filter(r => r.response.success);
      if (successfulResults.length > 0) {
        const fastestTime = Math.min(...successfulResults.map(r => r.response.responseTime));
        const cheapestCost = Math.min(...successfulResults.map(r => r.response.cost));

        comparisonResults.forEach(result => {
          if (result.response.success) {
            result.rank = sortedByScore.findIndex(r => r.model === result.model) + 1;
            result.relativeSpeed = (result.response.responseTime / fastestTime - 1) * 100;
            result.relativeCost = (result.response.cost / cheapestCost - 1) * 100;
          }
        });
      }

      setResults(comparisonResults);
      setWinner(sortedByScore.length > 0 ? sortedByScore[0].model : null);

      const totalDuration = Date.now() - startTime;
      const totalCost = comparisonResults.reduce((sum, r) => sum + r.response.cost, 0);
      const successCount = comparisonResults.filter(r => r.response.success).length;
      const successRate = (successCount / comparisonResults.length) * 100;

      // Update session with results
      await updateTestSession(sessionId, {
        totalDuration,
        totalCost,
        successRate
      });

      // Create session object for parent component
      const session: TestSession = {
        id: sessionId,
        timestamp: new Date().toISOString(),
        testCases: [],
        responses,
        totalDuration,
        totalCost,
        successRate
      };

      onSessionComplete(session);
      log.info('Model comparison completed', {
        successRate,
        totalCost,
        duration: totalDuration
      });

    } catch (error) {
      log.error('Model comparison failed', {}, error as Error);
      toast.error(`Comparison failed: ${(error as Error).message}`);
    } finally {
      setIsComparing(false);
    }
  };


  // Get rank badge color
  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) {
return 'bg-yellow-100 text-yellow-800';
}
    if (rank === 2) {
return 'bg-gray-100 text-gray-800';
}
    return 'bg-orange-100 text-orange-800';
  };

  return (
    <div className="space-y-4">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle>Model Comparison Setup</CardTitle>
          <CardDescription>
            Compare responses from different models side-by-side
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Query Input */}
          <div className="space-y-2">
            <Label htmlFor="comparison-query">Query</Label>
            <Textarea
              id="comparison-query"
              placeholder="Enter a query to compare across models..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              rows={3}
            />
          </div>

          {/* Agent Selection */}
          <div className="space-y-2">
            <Label>Agent</Label>
            <Select value={selectedAgent} onValueChange={(v) => setSelectedAgent(v as AgentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agents.map(agent => (
                  <SelectItem key={agent.value} value={agent.value}>
                    {agent.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Model Selection */}
          <div className="space-y-2">
            <Label>Select Models to Compare (minimum 2)</Label>
            <div className="grid grid-cols-3 gap-3">
              {models.map(model => (
                <div
                  key={model.value}
                  className={cn(
                    "border rounded-lg p-3 cursor-pointer transition-colors",
                    selectedModels.includes(model.value)
                      ? "bg-primary/10 border-primary"
                      : "hover:bg-muted"
                  )}
                  onClick={() => toggleModel(model.value)}
                >
                  <div className="flex items-start space-x-2">
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 mt-0.5",
                      selectedModels.includes(model.value)
                        ? "bg-primary border-primary"
                        : "border-muted-foreground"
                    )} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{model.label}</p>
                      <p className="text-xs text-muted-foreground">{model.speed}</p>
                      <Badge variant="outline" className="text-xs mt-1">
                        ${model.cost}/1M
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Run Comparison Button */}
          <Button
            className="w-full"
            onClick={runComparison}
            disabled={!query.trim() || selectedModels.length < 2 || isComparing}
          >
            {isComparing ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Comparing Models...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run Comparison
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Section */}
      {results.length > 0 && (
        <>
          {/* Winner Card */}
          {winner && (
            <Card className="border-yellow-200 bg-yellow-50/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Winner</CardTitle>
                  <Badge className="bg-yellow-100 text-yellow-800">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Best Overall
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Cpu className="h-5 w-5 text-yellow-600" />
                    <span className="font-medium">
                      {models.find(m => m.value === winner)?.label}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <Badge variant="outline">
                      Score: {((results.find(r => r.model === winner)?.response.score || 0) * 100).toFixed(0)}%
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comparison Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {results.map((result) => (
              <Card key={result.model} className={result.model === winner ? 'border-primary' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">
                      {models.find(m => m.value === result.model)?.label}
                    </CardTitle>
                    <Badge className={getRankBadgeColor(result.rank)}>
                      #{result.rank}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Response Preview */}
                  <div className="space-y-1">
                    <Label className="text-xs">Response</Label>
                    <ScrollArea className="h-[150px] w-full rounded-md border p-3">
                      <p className="text-sm whitespace-pre-wrap">
                        {result.response.response}
                      </p>
                    </ScrollArea>
                  </div>

                  {/* Metrics */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        <span>Speed</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>{(result.response.responseTime / 1000).toFixed(2)}s</span>
                        {result.relativeSpeed > 0 && (
                          <Badge variant="outline" className="text-xs">
                            +{result.relativeSpeed.toFixed(0)}%
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1">
                        <DollarSign className="h-3 w-3 text-muted-foreground" />
                        <span>Cost</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>${result.response.cost.toFixed(4)}</span>
                        {result.relativeCost > 0 && (
                          <Badge variant="outline" className="text-xs">
                            +{result.relativeCost.toFixed(0)}%
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1">
                        <Hash className="h-3 w-3 text-muted-foreground" />
                        <span>Tokens</span>
                      </div>
                      <span>
                        {result.response.tokensInput}/{result.response.tokensOutput}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1">
                        <FileText className="h-3 w-3 text-muted-foreground" />
                        <span>Docs Used</span>
                      </div>
                      <span>{result.response.documentsUsed?.length || 0}</span>
                    </div>
                  </div>

                  {/* Score Bar */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Quality Score</span>
                      <span>{((result.response.score || 0) * 100).toFixed(0)}%</span>
                    </div>
                    <Progress value={(result.response.score || 0) * 100} className="h-2" />
                  </div>

                  {/* Copy Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => navigator.clipboard.writeText(result.response.response)}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Response
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Comparison Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Comparison Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">Fastest Response</p>
                  <p className="font-medium">
                    {models.find(m => m.value === results.reduce((min, r) =>
                      r.response.responseTime < min.response.responseTime ? r : min
                    ).model)?.label}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Most Cost Effective</p>
                  <p className="font-medium">
                    {models.find(m => m.value === results.reduce((min, r) =>
                      r.response.cost < min.response.cost ? r : min
                    ).model)?.label}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Highest Quality</p>
                  <p className="font-medium">
                    {models.find(m => m.value === winner)?.label}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
